import React from 'react';
import { Button } from './ui/button';
import { Play, Sparkles, Feather } from 'lucide-react';

const Hero = ({ artistData }) => {
  if (!artistData) return null;

  const scrollToMusic = () => {
    const element = document.getElementById('music');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-black">
      {/* Sacred Geometry Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <circle cx="100" cy="100" r="80" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
            <circle cx="100" cy="100" r="60" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
            <circle cx="100" cy="100" r="40" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
            <circle cx="100" cy="100" r="20" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
          </svg>
        </div>
      </div>
      
      {/* Floating gold particles */}
      <div className="absolute top-20 right-20 w-2 h-2 bg-[#D4AF37] rounded-full sparkle"></div>
      <div className="absolute top-40 right-40 w-1 h-1 bg-[#FFB547] rounded-full sparkle" style={{ animationDelay: '0.5s' }}></div>
      <div className="absolute bottom-40 left-20 w-2 h-2 bg-[#C9A961] rounded-full sparkle" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-60 left-40 w-1 h-1 bg-[#D4AF37] rounded-full sparkle" style={{ animationDelay: '1.5s' }}></div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-3 px-5 py-3 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              <Feather className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Music Artist & Writer
              </span>
              <Sparkles className="w-4 h-4 text-[#FFB547]" />
            </div>
            
            {/* Artist Name in Script - WHITE */}
            <div className="space-y-6">
              <h1 className="text-8xl md:text-9xl artist-name text-white leading-none">
                {artistData.name}
              </h1>
              
              <div className="h-px w-40 bg-gradient-to-r from-[#D4AF37] to-transparent"></div>
              
              <p className="text-2xl md:text-3xl text-gray-300 font-light" style={{ fontFamily: 'Cinzel, serif' }}>
                {artistData.tagline}
              </p>
            </div>
            
            <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
              Weaving traditional ancient instruments with contemporary genres - a fusion of sound, symbolism, and storytelling through music and literary art.
            </p>
            
            <div className="flex flex-wrap items-center gap-4">
              <Button
                className="bg-[#D4AF37] hover:bg-[#FFB547] text-black border-0 px-8 py-6 text-base font-semibold group"
                onClick={scrollToMusic}
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" fill="currentColor" />
                Listen Now
              </Button>
              <Button
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-6 text-base font-semibold"
                onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
              >
                Discover Journey
              </Button>
            </div>
            
            {/* Quote */}
            <div className="pt-8 border-t border-[#D4AF37]/20">
              <p className="text-sm italic text-gray-500">
                "{artistData.quote}"
              </p>
            </div>
          </div>
          
          {/* Piano Hero Image with Sacred Geometry Frame */}
          <div className="relative">
            <div className="relative aspect-square overflow-hidden">
              {/* Golden sacred geometry border */}
              <div className="absolute inset-0 border-2 border-[#D4AF37]/30 rounded-lg"></div>
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#D4AF37]"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#D4AF37]"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#D4AF37]"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#D4AF37]"></div>
              
              {/* Piano Image */}
              <div className="absolute inset-2 rounded-lg overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/30641685/pexels-photo-30641685.jpeg"
                  alt="Piano in golden light"
                  className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500"
                />
                {/* Dark overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
              </div>
              
              {/* Floating sacred geometry decorations */}
              <div className="absolute -top-8 -right-8 w-24 h-24 opacity-20">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#D4AF37" strokeWidth="1"/>
                  <circle cx="50" cy="50" r="30" fill="none" stroke="#D4AF37" strokeWidth="1"/>
                  <circle cx="50" cy="50" r="20" fill="none" stroke="#D4AF37" strokeWidth="1"/>
                </svg>
              </div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 opacity-10">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <polygon points="50,10 90,90 10,90" fill="none" stroke="#D4AF37" strokeWidth="1"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
